/**
 * ApplicationPasswordModal — Step 3 of the signup wizard.
 *
 * Final gate before /auth/register. The visual chrome is provided by
 * <SignupWizardShell> so it matches Step 1 (Security Check) and
 * Step 2 (Email Verification).
 */
import React, { useState, useEffect, useRef } from 'react';
import { Lock, Eye, EyeOff, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import SignupWizardShell from './SignupWizardShell';

const APPLY_URL = 'https://www.collectiveorb.com/apply';

const ApplicationPasswordModal = ({ open, onClose, onSubmit, submitting = false }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [applyExpanded, setApplyExpanded] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setShowPassword(false);
      setApplyExpanded(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    onSubmit(password);
  };

  return (
    <SignupWizardShell
      open={open}
      step={3}
      icon={<Lock className="w-6 h-6" />}
      onClose={() => onClose?.('user_cancelled')}
      testId="app-password-modal"
    >
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
        <div className="relative">
          <Input
            id="app-password-input"
            ref={inputRef}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Application Password"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={submitting}
            data-testid="app-password-input"
            className="pr-10 bg-white text-slate-900"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            data-testid="app-password-toggle-visibility"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <Button
          type="submit"
          className="w-full bg-[#586D52] hover:bg-[#465A41] text-white h-11"
          disabled={submitting || !password.trim()}
          data-testid="app-password-submit-btn"
        >
          {submitting ? 'Verifying…' : 'Create Account'}
        </Button>

        <div className="pt-1">
          <button
            type="button"
            onClick={() => setApplyExpanded((v) => !v)}
            className="w-full text-center text-sm text-[#586D52] hover:text-[#3d4f3a] underline-offset-2 hover:underline flex items-center justify-center gap-1"
            data-testid="app-password-apply-toggle"
            aria-expanded={applyExpanded}
          >
            Need to submit an Application?
            <ChevronDown className={`w-4 h-4 transition-transform ${applyExpanded ? 'rotate-180' : ''}`} />
          </button>
          <div
            className={`grid transition-all duration-200 ease-out ${applyExpanded ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0 mt-0'}`}
          >
            <div className="overflow-hidden">
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 border-[#586D52] text-[#586D52] hover:bg-[#586D52]/10 bg-white"
                onClick={() => {
                  window.open(APPLY_URL, '_blank', 'noopener,noreferrer');
                }}
                data-testid="app-password-submit-application-btn"
              >
                Submit Application
              </Button>
            </div>
          </div>
        </div>
      </form>
    </SignupWizardShell>
  );
};

export default ApplicationPasswordModal;
